export type guid = string;

/**
 * Generate a new GUID (Globally Unique Identifier)
 * Format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
 * Where x is a hexadecimal digit (0-9, a-f)
 * Example: 3f2504e0-4f89-11d3-9a0c-0305e82c3301
 * ⚠️ This is not a cryptographically secure GUID and should NOT be used for security-sensitive purposes. If you need a secure GUID, use newSecureGuid() instead.
 * Use this method when you need a fast GUID generation for an ID internal to your application.
 * @return A new GUID as a string
 */
export function newGuid(): guid {
	return s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4() + s4() + s4();
}

/**
 * Generate a new cryptographically secure GUID (Globally Unique Identifier)
 * Format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
 * Where x is a hexadecimal digit (0-9, a-f)
 * Example: 3f2504e0-4f89-11d3-9a0c-0305e82c3301
 * This function uses the Web Crypto API for secure random number generation.
 * Use this method when you need a GUID for security-sensitive purposes, such as session identifiers or tokens.
 * @return A new secure GUID as a string
 */
export function newSecureGuid(): guid {
	return secureS4() + secureS4() + "-" + secureS4() + "-" + secureS4() + "-" + secureS4() + "-" + secureS4() + secureS4() + secureS4();
}

/**
 * Generate a new short GUID (22-character Base64 encoded string)
 * This function generates a standard GUID and then encodes it in Base64 format, resulting in a shorter representation.
 * The resulting string is URL-safe, replacing '/' with '_' and '+' with '-'.
 * ⚠️ This is not a cryptographically secure GUID and should NOT be used for security-sensitive purposes. If you need a secure GUID, use newSecureGuid() instead.
 * @return A new short GUID as a 22-character string
 */
export function newShortGuid(): guid {
	return guid_to_base64(newGuid(), true).replace(/\//g, "_").replace(/\+/g, "-").substring(0, 22);
}

/**
 * Detect global object (works in browser, worker, Node.js)
 */
function getGlobalObject(): typeof globalThis | undefined {
	if (typeof self !== "undefined") {
		return self;
	}
	if (typeof window !== "undefined") {
		return window;
	}
	if (typeof global !== "undefined") {
		return global;
	}
	return undefined;
}

function secureS4() {
	const globalObj = getGlobalObject();

	if (globalObj) {
		// Try modern crypto API (works in browsers and modern workers)
		if (globalObj.crypto?.getRandomValues) {
			const array = new Uint32Array(1);
			globalObj.crypto.getRandomValues(array);
			return (0x10000 + (array[0] % 0x10000)).toString(16).substring(1);
		}

		// Try legacy msCrypto API (IE11)
		if ((globalObj as any).msCrypto?.getRandomValues) {
			const array = new Uint32Array(1);
			(globalObj as any).msCrypto.getRandomValues(array);
			return (0x10000 + (array[0] % 0x10000)).toString(16).substring(1);
		}
	}

	// Fallback to Math.random(), less secure method
	return s4();
}

function s4() {
	return Math.floor((1 + Math.random()) * 0x10000)
		.toString(16)
		.substring(1);
}

// Convert GUID string to Base-64 in Javascript
// by Mark Seecof, 2012-03-31
const hexlist = "0123456789abcdef";
const b64list = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

// GUID string with four dashes is always MSB first,
// but base-64 GUID's vary by target-system endian-ness.
// Little-endian systems are far more common.  Set le==true
// when target system is little-endian (e.g., x86 machine).
//
function guid_to_base64(g, le) {
	let s = g.replace(/[^0-9a-f]/gi, "").toLowerCase();
	if (s.length != 32) return "";
	if (le) s = s.slice(6, 8) + s.slice(4, 6) + s.slice(2, 4) + s.slice(0, 2) + s.slice(10, 12) + s.slice(8, 10) + s.slice(14, 16) + s.slice(12, 14) + s.slice(16);
	s += "0";

	let a, p, q;
	let r = "";
	let i = 0;
	while (i < 33) {
		a = (hexlist.indexOf(s.charAt(i++)) << 8) | (hexlist.indexOf(s.charAt(i++)) << 4) | hexlist.indexOf(s.charAt(i++));

		p = a >> 6;
		q = a & 63;
		r += b64list.charAt(p) + b64list.charAt(q);
	}
	r += "==";

	return r;
}
